/** leads/CapitalPage.tsx — ① 증자 업체: 업로드(엑셀/CSV) → 벤처 자동 대조 → 리드 자동생성/검토 확정
 *  2026-09-10 올댓벤처스 「자본금 증자업체」 표와 같은 열·검색·열 폭 규칙 — 회사당 1행(연도 나열 · 금액 합산 · 건수 · 대조상태).
 *  영업 DB 고유 기능 유지: 대조 실행 · 사업자번호 자동 확보 · 번호 입력 후 리드 확정 · 선택 삭제 · 업로드 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import './leads.css';
import { listCapitalCompanies, capitalMeta, capitalBulk, runCapitalMatch, confirmCapital, deleteCapitals, capitalEnrichBizNo } from './api';
import { parseCapRows } from './capParse';
import { fmtBizNo, fmtCorpNo } from './calc';
import type { CapitalCompany, CapitalMeta } from './types';
import AtvTable, { AtvPager, fmtMoney } from './AtvTable';
import type { AtvCol } from './AtvTable';
import AtvFilterCard from './AtvFilterCard';
import type { Chip } from './AtvFilterCard';

interface Props { onToast: (m: string, t?: string) => void }
interface F { page: number; pageSize: number; q?: string; year?: number; region?: string; sigungu?: string; status: string }

export default function CapitalPage({ onToast }: Props) {
  const [rows, setRows] = useState<CapitalCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [meta, setMeta] = useState<CapitalMeta | null>(null);
  const [f, setF] = useState<F>({ page: 1, pageSize: 50, status: 'all' });
  const [sel, setSel] = useState<Set<string>>(new Set()); // 증자 건 id 집합 (회사 행 = 그 회사 건 전부)
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bizInput, setBizInput] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listCapitalCompanies(f);
      setRows(d.rows); setTotal(d.total); setSel(new Set());
      if (d.rows.length === 0 && d.total > 0 && f.page > 1) setF((p) => ({ ...p, page: 1 }));
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setLoading(false); }
  }, [f, onToast]);
  const loadMeta = useCallback(async () => {
    try { setMeta(await capitalMeta()); } catch (e) { onToast((e as Error).message, 'error'); }
  }, [onToast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMeta(); }, [loadMeta]);
  const set = (patch: Partial<F>) => setF((p) => ({ ...p, ...patch, page: 1 }));

  const onUpload = async (file: File) => {
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const objs = XLSX.utils.sheet_to_json<Record<string, unknown>>(wb.Sheets[wb.SheetNames[0]], { defval: '' });
      const { rows: parsedRows, skipped } = parseCapRows(objs);
      if (parsedRows.length === 0) { onToast('유효 행이 없습니다 — 헤더(법인명·증자일/연도) 확인', 'error'); return; }
      let inserted = 0; let dup = 0;
      for (let i = 0; i < parsedRows.length; i += 2000) {
        onToast('업로드 중… ' + Math.min(i + 2000, parsedRows.length) + '/' + parsedRows.length);
        const r = await capitalBulk(file.name, parsedRows.slice(i, i + 2000));
        inserted += r.inserted; dup += r.skipped;
      }
      onToast('신규 ' + inserted.toLocaleString() + '건 · 중복 ' + dup + '건 · 불량 ' + skipped + '건 — [벤처 대조 실행]을 누르세요');
      load(); loadMeta();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const doMatch = async () => {
    setBusy(true); onToast('벤처기업 명단과 대조 중…');
    try {
      const r = await runCapitalMatch();
      onToast('대조 완료 — 매칭 ' + r.matched + '건 / 리드 자동생성 ' + r.auto_leads + '건' + (r.existed_links > 0 ? ' (기존연결 ' + r.existed_links + ')' : '') + ' / 번호 필요 ' + r.needs_biz_no + '건' + (r.ambiguous > 0 ? ' / 동명 검토 ' + r.ambiguous + '건' : ''));
      load(); loadMeta();
    } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const doConfirm = async (c: CapitalCompany) => {
    const digits = (bizInput[c.ckey] || '').replace(/[^0-9]/g, '');
    if (digits.length !== 10) { onToast('사업자번호 10자리를 입력하세요', 'error'); return; }
    const target = c.items.find((it) => it.matched && !it.lead_id) || c.items[0];
    setBusy(true);
    try { await confirmCapital(target.id, digits); onToast(c.corp_name + ' → 리드 확정 완료'); load(); loadMeta(); }
    catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  const doDelete = async () => {
    if (sel.size === 0 || !window.confirm('선택 ' + sel.size + '건(증자 건)을 삭제할까요? (생성된 리드는 유지)')) return;
    try { const r = await deleteCapitals(Array.from(sel)); onToast(r.deleted + '건 삭제'); load(); loadMeta(); }
    catch (e) { onToast((e as Error).message, 'error'); }
  };
  const idsOf = (c: CapitalCompany) => c.items.map((it) => it.id);
  const rowSel = (c: CapitalCompany) => idsOf(c).every((id) => sel.has(id));
  const toggleRow = (c: CapitalCompany) => setSel((s) => { const n = new Set(s); const ids = idsOf(c); const all = ids.every((id) => n.has(id)); ids.forEach((id) => { if (all) n.delete(id); else n.add(id); }); return n; });
  const allIds = rows.flatMap(idsOf);
  const allSel = allIds.length > 0 && allIds.every((id) => sel.has(id));

  const badge = (c: CapitalCompany) => {
    if (c.has_lead) return <span className="atv-chip" style={{ background: '#EAF6EC', color: '#1B7A3D' }}>경정청구 대상</span>;
    if (c.needs_biz_no) return <span className="atv-chip" style={{ background: '#FFF7ED', color: '#EA580C' }}>벤처 매칭 — 번호 필요</span>;
    if (c.ambiguous) return <span className="atv-chip" style={{ background: '#F5F3FF', color: '#7C3AED' }}>동명 검토</span>;
    if (c.out_period) return <span className="atv-chip" style={{ background: '#F0F9FF', color: '#0369A1' }} title="벤처 유효기간 밖 증자 — 경정청구 불가, 벤처확인·정책자금 영업 시그널">기간 외 · 영업 시그널</span>;
    return <span style={{ color: '#6B7280' }}>미대조</span>;
  };

  const chips: Chip[] = [];
  if (f.q) chips.push({ k: '검색', v: f.q, rm: () => set({ q: undefined }) });
  if (f.year) chips.push({ k: '투자연도', v: String(f.year), rm: () => set({ year: undefined }) });
  if (f.region) chips.push({ k: '지역', v: f.region + (f.sigungu ? ' ' + f.sigungu : ''), rm: () => set({ region: undefined, sigungu: undefined }) });
  if (f.status !== 'all') chips.push({ k: '대조', v: ({ unmatched: '미대조', needs_biz_no: '번호 필요', lead: '경정청구 대상', out_of_period: '기간 외' } as Record<string, string>)[f.status] || f.status, rm: () => set({ status: 'all' }) });

  const cols: AtvCol<CapitalCompany>[] = [
    { key: 'bizno', label: '사업자번호', w: 94, render: (c) => (c.biz_no ? fmtBizNo(c.biz_no) : <span style={{ color: '#D1D5DB' }}>—</span>) },
    { key: 'corpno', label: '법인등록번호', w: 110, render: (c) => (c.corp_no ? fmtCorpNo(c.corp_no) : <span style={{ color: '#D1D5DB' }}>—</span>) },
    { key: 'region', label: '지역', w: 96, render: (c) => [c.region, c.sigungu].filter(Boolean).join(' ') },
    { key: 'addr', label: '주소', w: 128, absorb: true, render: (c) => <span className="atv-addr" title={c.address || ''}>{c.address || ''}</span> },
    { key: 'years', label: '투자연도', w: 88, render: (c) => (c.years || []).join('·') },
    { key: 'amount', label: '투자금액', w: 82, cls: 'num', render: (c) => <>{fmtMoney(c.amount_total)}{c.cnt > 1 ? <span style={{ color: '#6B7280', fontSize: 10 }}> {c.cnt}건</span> : null}</> },
    { key: 'match', label: '대조상태', w: 130, render: badge },
    { key: 'act', label: '리드 확정', w: 200, render: (c) => (c.needs_biz_no && !c.has_lead ? (
      <span style={{ display: 'inline-flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
        <input style={{ width: 112, padding: '3px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12, fontFamily: 'inherit' }} placeholder="사업자번호 입력" value={bizInput[c.ckey] || ''}
          onChange={(e) => setBizInput((m) => ({ ...m, [c.ckey]: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') doConfirm(c); }} />
        <button type="button" className="atv-abtn pri" disabled={busy} onClick={() => doConfirm(c)}>리드 확정</button>
      </span>) : null) },
  ];

  return (
    <div className="ld-root">
      <div className="ld-counts">
        {([['all', '전체', meta?.total], ['unmatched', '미대조', meta ? meta.total - meta.matched - meta.out_period : undefined], ['needs_biz_no', '번호 필요', meta?.needs_biz_no], ['lead', '경정청구 대상', meta?.leads], ['out_of_period', '기간 외', meta?.out_period]] as [string, string, number | undefined][]).map(([k, l, n]) => (
          <button key={k} className={'ld-count' + (f.status === k ? ' on' : '')} onClick={() => set({ status: k })}>{l} {(n || 0).toLocaleString()}<span style={{ fontSize: 10, opacity: .7 }}>건</span></button>
        ))}
        <span style={{ fontSize: 11, color: '#4B5563', alignSelf: 'center' }}>대조 규칙: 법인명 일치 + 증자연도가 벤처 유효기간 안 · 사업자번호 있으면 리드 자동생성 · 건수 = 증자 건</span>
      </div>

      <AtvFilterCard
        searchPlaceholder="회사명 · 사업자번호 · 주소 검색"
        q={f.q || ''} onQ={(q) => set({ q: q || undefined })}
        find={(<>
          <select className="atv-fsel" value={f.year || ''} onChange={(e) => set({ year: e.target.value ? Number(e.target.value) : undefined })}>
            <option value="">투자연도 전체</option>
            {(meta?.years || []).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select className="atv-fsel" value={f.region || ''} onChange={(e) => set({ region: e.target.value || undefined, sigungu: undefined })}>
            <option value="">지역 전체</option>
            {(meta?.regions || []).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {f.region && (
            <select className="atv-fsel" value={f.sigungu || ''} onChange={(e) => set({ sigungu: e.target.value || undefined })}>
              <option value="">시군구 전체</option>
              {(meta?.sigungus || []).filter((x) => x.region === f.region).map((x) => <option key={x.sigungu} value={x.sigungu}>{x.sigungu}</option>)}
            </select>
          )}
        </>)}
        view={(
          <select className="atv-fsel" value={f.pageSize} onChange={(e) => set({ pageSize: Number(e.target.value) })} title="표시 개수">
            <option value={50}>50개씩</option><option value={500}>500개씩</option><option value={1000}>1000개씩</option>
          </select>
        )}
        total={total} grandTotal={null} chips={chips} onResetAll={() => setF((p) => ({ page: 1, pageSize: p.pageSize, status: 'all' }))} selectedCount={sel.size} loading={loading}
      />

      <div className="ld-toolbar" style={{ marginBottom: 10 }}>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" hidden onChange={(e) => { const file = e.target.files?.[0]; if (file) onUpload(file); e.target.value = ''; }} />
        <button className="ld-btn ok" disabled={busy || !meta || meta.total === 0} onClick={doMatch}>벤처 대조 실행</button>
        <button className="ld-btn" disabled={busy} title="상호+지역 판별로 개요 API에서 사업자번호·법인번호 확보 — 벤처 매칭건은 리드까지 자동"
          onClick={async () => { setBusy(true); try { const r = await capitalEnrichBizNo(sel.size ? Array.from(sel) : undefined);
            onToast('사업자번호 ' + r.found + '건 확보 · 리드 ' + r.leads + '건 생성' + (r.ambiguous ? ' · 동명검토 ' + r.ambiguous : '') + (r.not_found ? ' · 미확인 ' + r.not_found : ''));
            load(); loadMeta(); } catch (e) { onToast((e as Error).message, 'error'); } finally { setBusy(false); } }}>사업자번호 자동 확보</button>
        <span style={{ flex: 1 }} />
        {sel.size > 0 && <button className="ld-btn" style={{ color: '#DC2626', borderColor: '#FECACA' }} onClick={doDelete}>선택 {sel.size}건 삭제</button>}
      </div>

      <AtvTable<CapitalCompany>
        storeKey="roum/pool-capital"
        rows={rows}
        rowKey={(c) => c.ckey}
        lead={[
          { key: 'chk', fw: 38, header: <input type="checkbox" title="화면 전체 선택" checked={allSel} onChange={() => setSel(allSel ? new Set() : new Set(allIds))} />, stopClick: true, render: (c) => <input type="checkbox" checked={rowSel(c)} onChange={() => toggleRow(c)} /> },
          { key: 'no', fw: 38, header: 'No', cls: 'atv-no', render: (_c, i) => String((f.page - 1) * f.pageSize + i + 1) },
        ]}
        name={{ label: '회사명', w: 196, render: (c) => <span className="atv-co"><span className="nm" title={c.corp_name}>{c.corp_name}</span></span> }}
        cols={cols}
        empty={loading ? '불러오는 중…' : '증자 파일을 업로드하고 [벤처 대조 실행]을 누르세요'}
      />
      <AtvPager total={total} page={f.page} pageSize={f.pageSize} onPage={(p) => setF((x) => ({ ...x, page: p }))}
        left={<button disabled={busy} onClick={() => fileRef.current?.click()} title="외부 증자 명단 업로드 (엑셀/CSV — 스캔 적재와 자동 병합)" style={{ marginRight: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C0C4CC', textDecoration: 'underline dotted' }}>증자 파일 업로드</button>} />
      <div className="atv-footnote">· 여러 해에 걸쳐 증자한 회사는 연도를 나열하고 금액을 합산해 한 줄로 표시합니다 (건수 = 증자 건) · 연도별 상세는 경정청구 대상 리스트의 상세에서 확인하세요 · 열 경계선을 끌어 폭 조절(더블클릭 기본 폭 · Shift+더블클릭 전체 초기화)</div>
    </div>
  );
}
